import clsx from 'clsx';
import React from 'react';
import Spacer from '../Spacer';
import { Icon, Icons } from 'components/Icon';

type VariantTypes = 'primary' | 'secondary' | 'outline' | 'transparent';
type SizeTypes = 'sm' | 'md' | 'lg' | 'xl' | 'custom';

interface ButtonProps {
  variant?: VariantTypes;
  size?: SizeTypes;
  label?: string;
  onClick?: (arg: any) => void;
  type?: 'button' | 'submit';
  icon?: React.ReactNode;
  width?: number;
  height?: number;
  loading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  textColor?: string;
  capitalized?: boolean;
  className?: any;
  iconPosition?: 'left' | 'right';
  resizeOnHover?: boolean;
  iconLabelSpacing?: number;
  hoverEffectDisabled?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  variant,
  label,
  onClick = () => {},
  type = 'button',
  icon,
  width,
  height,
  loading = false,
  disabled = false,
  textColor,
  capitalized,
  className,
  size, // If we have size, we should not have variant as it overrides the width of what is set on additional className
  iconPosition = 'left',
  resizeOnHover = false,
  iconLabelSpacing = 16,
  loadingText,
  hoverEffectDisabled
}) => {
  const computedStyle = () => {
    const style: { width?: string; height?: string; color?: string } = {};

    if (width) {
      style.width = `${width}px`;
    }

    if (height) {
      style.height = `${height}px`;
    }

    if (textColor) {
      style.color = textColor;
    }

    return style;
  };

  return (
    <button
      className={clsx({
        'flex items-center justify-center cursor-pointer': true,
        'h-[40px]': !size || ['lg', 'md'].includes(size),
        'h-[32px]': size === 'sm',
        'h-[57px] w-full': size === 'lg',
        'w-[126px] h-[32px]': size === 'md',
        'w-[90px] h-[32px]': size === 'sm',
        'bg-[#FF6600] text-[#fff] box-content': variant === 'primary',
        // 'bg-[#fdfdfd]': variant && ['outline'].includes(variant),
        'text-[#7F8B94] border border-solid border-[#7F8B94]':
          variant === 'secondary',
        'border border-[#FF7A00]': variant === 'outline',
        'hover:bg-[#FF7A00] hover:text-white':
          variant === 'primary' && !disabled && !hoverEffectDisabled,
        'opacity-50 hover:none cursor-not-allowed': disabled,
        'bg-transparent outline-none border-none': variant === 'transparent',
        'hover:w-[135px]': resizeOnHover === true,
        capitalize: capitalized,
        [className]: className
      })}
      onClick={onClick}
      type={type}
      style={computedStyle()}
      disabled={disabled}
    >
      {loading && <Icon name={Icons.ActivityIndicator} />}
      {icon && iconPosition === 'left' && !loading && (
        <>
          {icon}
          <Spacer width={iconLabelSpacing} />
        </>
      )}
      {!loading ? label : !loadingText ? 'Loading...' : `${loadingText}...`}
      {icon && iconPosition === 'right' && !loading && (
        <>
          <Spacer width={iconLabelSpacing} />
          {icon}
        </>
      )}
    </button>
  );
};

export default Button;
